import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Grid, Card, Typography, Button, CircularProgress, alpha } from '@mui/material';
import {
  ShieldOutlined,
  FamilyRestroomOutlined,
  HealthAndSafetyOutlined,
  PaymentsOutlined,
  Add,
} from '@mui/icons-material';
import { useSupabase } from '../../contexts/SupabaseContext';
import { summarisePolicies, dueInfo, formatCover, formatRupees, formatDate, DUE_SOON_DAYS } from '../../utils/insurance';
import SectionTitle, { CardHeading, dashboardCardSx } from './SectionTitle';

const Stat = ({ icon, label, value, color }) => (
  <Box sx={{ textAlign: 'center', py: 1, px: 0.5, minWidth: 0 }}>
    <Box sx={{ color, display: 'flex', justifyContent: 'center', mb: 0.25, '& svg': { fontSize: 20 } }}>{icon}</Box>
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }} noWrap>{label}</Typography>
    <Typography variant="subtitle1" sx={{ fontWeight: 600, color, fontVariantNumeric: 'tabular-nums' }} noWrap>{value}</Typography>
  </Box>
);

// Dashboard → Insurance: cover and premiums of active policies, and what's due soon
const InsuranceOverview = () => {
  const navigate = useNavigate();
  const { supabase, user } = useSupabase();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    supabase
      .from('insurance_policies')
      .select('id, policy_name, policy_type, sum_assured, premium_amount, premium_frequency, next_due_date, status')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .then(({ data, error }) => {
        if (cancelled) return;
        // e.g. the insurance tables haven't been created yet: show the empty prompt
        if (error) console.error('Error fetching insurance policies:', error);
        setPolicies(data || []);
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [supabase, user]);

  const totals = useMemo(() => summarisePolicies(policies), [policies]);
  const due = useMemo(() => policies
    .map(p => ({ policy: p, info: dueInfo(p) }))
    .filter(d => d.info)
    .sort((a, b) => a.info.days - b.info.days), [policies]);
  const dueSoon = due.filter(d => d.info.days <= DUE_SOON_DAYS);
  const nextDue = due[0];
  const hasOverdue = dueSoon.some(d => d.info.days < 0);
  const dueColor = hasOverdue ? 'error' : dueSoon.length ? 'warning' : 'success';

  return (
    <Box sx={{ mb: 5 }}>
      <SectionTitle>Insurance</SectionTitle>
      {!loading && policies.length === 0 ? (
        <Card data-tour="dashboard-insurance" sx={{ ...dashboardCardSx, p: { xs: 2, sm: 3 }, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ width: 48, height: 48, borderRadius: '50%', display: 'grid', placeItems: 'center', bgcolor: (t) => alpha(t.palette.success.main, 0.1), color: 'success.main', flexShrink: 0 }}>
            <ShieldOutlined />
          </Box>
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <Typography sx={{ fontWeight: 600 }}>No active insurance policies</Typography>
            <Typography variant="body2" color="text.secondary">Add your term, health and motor policies to see your cover and upcoming premiums here.</Typography>
          </Box>
          <Button variant="outlined" startIcon={<Add />} onClick={() => navigate('/insurance')}>Add Policy</Button>
        </Card>
      ) : (
        <Grid container spacing={3}>
          <Grid item xs={12} md={9}>
            <Card data-tour="dashboard-insurance" onClick={() => navigate('/insurance')} sx={{ ...dashboardCardSx, cursor: 'pointer', p: { xs: 2, sm: 3 }, minHeight: 160, height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardHeading color="linear-gradient(45deg, #16a34a, #15803d)">Your Cover</CardHeading>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}><CircularProgress size={24} /></Box>
              ) : (
                <Box sx={{ flex: 1, display: 'grid', alignContent: 'center', gridTemplateColumns: { xs: 'repeat(3, 1fr)', sm: 'repeat(5, 1fr)' }, gap: 1 }}>
                  <Stat icon={<ShieldOutlined />} label="Active Policies" value={totals.count} color="text.primary" />
                  <Stat icon={<ShieldOutlined />} label="Total Cover" value={formatCover(totals.cover)} color="success.main" />
                  <Stat icon={<FamilyRestroomOutlined />} label="Life Cover" value={formatCover(totals.lifeCover)} color="primary.main" />
                  <Stat icon={<HealthAndSafetyOutlined />} label="Health Cover" value={formatCover(totals.healthCover)} color="success.dark" />
                  <Stat icon={<PaymentsOutlined />} label="Premium / Year" value={formatRupees(totals.annualPremium)} color="info.main" />
                </Box>
              )}
            </Card>
          </Grid>
          <Grid item xs={12} md={3}>
            <Card
              data-tour="dashboard-insurance-due"
              onClick={() => navigate(dueSoon.length === 1 ? `/insurance/${dueSoon[0].policy.id}` : '/insurance')}
              sx={{ ...dashboardCardSx, cursor: 'pointer', p: { xs: 2, sm: 3 }, minHeight: 160, height: '100%', display: 'flex', flexDirection: 'column' }}
            >
              <CardHeading color={hasOverdue ? 'linear-gradient(45deg, #F44336, #D32F2F)' : dueSoon.length ? 'linear-gradient(45deg, #FF9800, #F57C00)' : 'linear-gradient(45deg, #4CAF50, #388E3C)'}>
                Premiums Due
              </CardHeading>
              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}><CircularProgress size={24} /></Box>
              ) : (
                <Box sx={{
                  flex: 1, textAlign: 'center', p: 2, borderRadius: 2, display: 'flex', flexDirection: 'column', justifyContent: 'center',
                  bgcolor: (t) => alpha(t.palette[dueColor].main, 0.07), border: '1px solid', borderColor: (t) => alpha(t.palette[dueColor].main, 0.2),
                }}>
                  {dueSoon.length ? (
                    <>
                      <Typography variant="h6" sx={{ fontWeight: 600, color: `${dueColor}.main`, fontVariantNumeric: 'tabular-nums' }}>
                        {formatRupees(totals.dueAmount)}
                      </Typography>
                      <Typography variant="caption" sx={{ color: `${dueColor}.main`, fontWeight: 500 }}>
                        {hasOverdue ? 'Overdue or due' : 'Due'} in {DUE_SOON_DAYS} days · {dueSoon.length} {dueSoon.length === 1 ? 'policy' : 'policies'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                        {dueSoon[0].policy.policy_name}: {dueSoon[0].info.label.toLowerCase()}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: 'success.main' }}>Nothing due in {DUE_SOON_DAYS} days</Typography>
                      {nextDue && (
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                          Next: {nextDue.policy.policy_name} on {formatDate(nextDue.policy.next_due_date)}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              )}
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default InsuranceOverview;
